// Currency formatter for Chilean Peso
export const formatCurrency = (amount, currency = 'CLP') => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '$0'
  }
  
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(Number(amount))
}

// Number formatter
export const formatNumber = (number) => {
  if (number === null || number === undefined || isNaN(number)) {
    return '0'
  }
  
  return new Intl.NumberFormat('es-CL').format(Number(number))
}

// Date formatter
export const formatDate = (date, options = {}) => {
  if (!date) return '-'
  
  const defaultOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'America/Santiago'
  }
  
  const formatOptions = { ...defaultOptions, ...options }
  
  try {
    return new Intl.DateTimeFormat('es-CL', formatOptions).format(new Date(date))
  } catch (error) {
    return '-'
  }
}

// DateTime formatter
export const formatDateTime = (date) => {
  if (!date) return '-'
  
  try {
    return new Intl.DateTimeFormat('es-CL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/Santiago'
    }).format(new Date(date))
  } catch (error) {
    return '-'
  }
}

// Relative time formatter (e.g., "hace 2 días")
export const formatRelativeTime = (date) => {
  if (!date) return '-'
  
  try {
    const now = new Date()
    const targetDate = new Date(date)
    const diffInSeconds = Math.floor((now - targetDate) / 1000)
    
    if (diffInSeconds < 60) {
      return 'hace unos segundos'
    }
    
    const diffInMinutes = Math.floor(diffInSeconds / 60)
    if (diffInMinutes < 60) {
      return `hace ${diffInMinutes} minuto${diffInMinutes !== 1 ? 's' : ''}`
    }
    
    const diffInHours = Math.floor(diffInMinutes / 60)
    if (diffInHours < 24) {
      return `hace ${diffInHours} hora${diffInHours !== 1 ? 's' : ''}`
    }
    
    const diffInDays = Math.floor(diffInHours / 24)
    if (diffInDays < 30) {
      return `hace ${diffInDays} día${diffInDays !== 1 ? 's' : ''}`
    }
    
    const diffInMonths = Math.floor(diffInDays / 30)
    if (diffInMonths < 12) {
      return `hace ${diffInMonths} mes${diffInMonths !== 1 ? 'es' : ''}`
    }
    
    const diffInYears = Math.floor(diffInMonths / 12)
    return `hace ${diffInYears} año${diffInYears !== 1 ? 's' : ''}`
  } catch (error) {
    return '-'
  }
}

// Days until/since formatter
export const formatDaysUntil = (date) => {
  if (!date) return null
  
  try {
    const now = new Date()
    const targetDate = new Date(date)
    const diffInMs = targetDate - now
    const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24))
    
    if (diffInDays === 0) {
      return 'Hoy'
    } else if (diffInDays === 1) {
      return 'Mañana'
    } else if (diffInDays === -1) {
      return 'Ayer'
    } else if (diffInDays > 0) {
      return `En ${diffInDays} días`
    } else {
      return `Hace ${Math.abs(diffInDays)} días`
    }
  } catch (error) {
    return null
  }
}

// Percentage formatter
export const formatPercentage = (value, decimals = 1) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0%'
  }
  
  return `${Number(value).toFixed(decimals)}%`
}

// Invoice status formatter
export const formatInvoiceStatus = (status) => {
  const statusMap = {
    'POR_COBRAR': 'Por Cobrar',
    'PAGADA': 'Pagada',
    'VENCIDA': 'Vencida'
  }
  
  return statusMap[status] || status
}

// User role formatter
export const formatUserRole = (role) => {
  const roleMap = {
    'ADMINISTRADOR': 'Administrador',
    'CONSULTA': 'Consulta'
  }
  
  return roleMap[role] || role
}

// File size formatter
export const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 Bytes'
  
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

// RUT formatter (Chilean ID)
export const formatRUT = (rut) => {
  if (!rut) return ''
  
  // Remove any existing formatting
  const cleanRUT = rut.replace(/[^0-9kK]/g, '')
  
  if (cleanRUT.length < 2) return cleanRUT
  
  // Split number and verification digit
  const number = cleanRUT.slice(0, -1)
  const verifier = cleanRUT.slice(-1).toUpperCase()
  
  // Add dots to number
  const formattedNumber = number.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
  
  return `${formattedNumber}-${verifier}`
}

// Phone number formatter (Chilean format)
export const formatPhone = (phone) => {
  if (!phone) return ''
  
  // Remove any existing formatting
  const cleanPhone = phone.replace(/[^0-9]/g, '')
  
  if (cleanPhone.length === 9) {
    // Mobile format: +56 9 XXXX XXXX
    return `+56 9 ${cleanPhone.slice(1, 5)} ${cleanPhone.slice(5)}`
  } else if (cleanPhone.length === 8) {
    // Landline format: +56 XX XXX XXXX
    return `+56 ${cleanPhone.slice(0, 2)} ${cleanPhone.slice(2, 5)} ${cleanPhone.slice(5)}`
  }
  
  return phone
}

// Truncate text
export const truncateText = (text, maxLength = 50) => {
  if (!text) return ''
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

// Capitalize first letter
export const capitalize = (text) => {
  if (!text) return ''
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()
}

// Format invoice number with prefix
export const formatInvoiceNumber = (number, prefix = 'F-') => {
  if (!number) return ''
  return `${prefix}${number}`
}
